import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { MediaUploader, type UploaderItem } from "@/components/admin/media-uploader";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	type Banner,
	type BannerPayload,
	type BannerPlacement,
	type BannerTemplate,
	PLACEMENT_LABELS,
	TEMPLATE_LABELS,
} from "@/lib/marketing/banners-api";

import { BannerPreview } from "./banner-preview";

/** Palette de la charte — on ne saisit pas une couleur libre, on la choisit. */
const PALETTE = ["#C6563A", "#1E4D3B", "#D4A24C", "#A04428", "#2B6A8E", "#8F3B26"];

/** Champs pertinents selon le modèle : chacun n'affiche que ce qu'il utilise. */
const CHAMPS: Record<
	BannerTemplate,
	{
		discount?: boolean;
		code?: boolean;
		product?: boolean;
		thumbs?: boolean;
		categories?: boolean;
	}
> = {
	hero: {},
	promo: { discount: true, code: true },
	product: { product: true },
	collection: { thumbs: true },
	category: { categories: true },
	app: {},
};

interface Brouillon {
	template: BannerTemplate;
	placement: BannerPlacement;
	title: string;
	eyebrow: string;
	ctaLabel: string;
	ctaHref: string;
	color: string;
	imageUrl: string;
	imageMobileUrl: string;
	imageAlt: string;
	productSlug: string;
	discount: string;
	code: string;
	thumbs: string;
	categorySlugs: string;
	startsAt: string;
	endsAt: string;
	durationSec: number;
}

const depuisBanner = (b: Banner | null, template: BannerTemplate): Brouillon => ({
	template: b?.template ?? template,
	placement: b?.placement ?? "hero_homepage",
	title: b?.title ?? "",
	eyebrow: b?.eyebrow ?? "",
	ctaLabel: b?.ctaLabel ?? "Découvrir",
	ctaHref: b?.ctaHref ?? "",
	color: b?.color ?? PALETTE[0],
	imageUrl: b?.imageUrl ?? "",
	imageMobileUrl: b?.imageMobileUrl ?? "",
	imageAlt: b?.imageAlt ?? "",
	productSlug: b?.productSlug ?? "",
	discount: b?.discount ?? "",
	code: b?.code ?? "",
	thumbs: (b?.thumbs ?? []).join(", "),
	categorySlugs: (b?.categorySlugs ?? []).join(", "),
	startsAt: b?.startsAt ? b.startsAt.slice(0, 10) : "",
	endsAt: b?.endsAt ? b.endsAt.slice(0, 10) : "",
	durationSec: b?.durationSec ?? 5,
});

const liste = (s: string) =>
	s
		.split(",")
		.map((x) => x.trim())
		.filter(Boolean);

/**
 * Formulaire d'une bannière, avec aperçu vivant.
 *
 * L'aperçu est à gauche et se met à jour pendant la saisie : une bannière se
 * juge à l'œil, pas à la lecture de ses champs. Sans lui, il fallait
 * enregistrer, aller voir le site, revenir corriger.
 */
export function BannerFormModal({
	banner,
	template,
	onClose,
	onSubmit,
	saving,
}: {
	banner: Banner | null;
	template: BannerTemplate;
	onClose: () => void;
	onSubmit: (payload: BannerPayload & { template: BannerTemplate; title: string }) => void;
	saving?: boolean;
}) {
	const [v, setV] = useState<Brouillon>(() => depuisBanner(banner, template));
	const champs = CHAMPS[v.template];
	const maj = <K extends keyof Brouillon>(k: K, valeur: Brouillon[K]) =>
		setV((etat) => ({ ...etat, [k]: valeur }));

	const image = (items: UploaderItem[]) => {
		const premier = items[0];
		maj("imageUrl", premier?.url ?? "");
		if (premier?.altText) maj("imageAlt", premier.altText);
	};

	const valider = () => {
		onSubmit({
			template: v.template,
			placement: v.placement,
			title: v.title.trim(),
			eyebrow: v.eyebrow.trim() || null,
			ctaLabel: v.ctaLabel.trim() || null,
			ctaHref: v.ctaHref.trim() || null,
			color: v.color,
			imageUrl: v.imageUrl || null,
			imageMobileUrl: v.imageMobileUrl || null,
			imageAlt: v.imageAlt.trim() || null,
			productSlug: champs.product ? v.productSlug.trim() || null : null,
			discount: champs.discount ? v.discount.trim() || null : null,
			code: champs.code ? v.code.trim() || null : null,
			thumbs: champs.thumbs ? liste(v.thumbs) : [],
			categorySlugs: champs.categories ? liste(v.categorySlugs) : [],
			startsAt: v.startsAt ? new Date(v.startsAt).toISOString() : null,
			endsAt: v.endsAt ? new Date(`${v.endsAt}T23:59:59`).toISOString() : null,
			durationSec: v.durationSec,
		});
	};

	const tpl = TEMPLATE_LABELS[v.template];

	return (
		<Modal
			title={banner ? `Modifier · ${tpl.name}` : `Nouvelle bannière · ${tpl.name}`}
			subtitle={tpl.desc}
			onClose={onClose}
			width={880}
		>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "minmax(0, 300px) minmax(0, 1fr)",
					gap: 18,
					alignItems: "start",
				}}
			>
				<div>
					<div
						className="muted"
						style={{
							fontSize: 10,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.08em",
							marginBottom: 8,
						}}
					>
						Aperçu
					</div>
					<div
						style={{
							border: "1px solid var(--a-border)",
							borderRadius: 10,
							overflow: "hidden",
						}}
					>
						<BannerPreview
							b={{
								...v,
								// Le brouillon garde les listes en texte saisissable ;
								// l'aperçu, lui, attend des tableaux.
								thumbs: liste(v.thumbs),
								categorySlugs: liste(v.categorySlugs),
							}}
						/>
					</div>
					<p
						className="muted"
						style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}
					>
						Rendu approché : la vitrine applique sa propre typographie et
						s'adapte à la taille de l'écran.
					</p>
				</div>

				<div>
					<FormRow label="Titre" required>
						<input
							className="aform-i"
							value={v.title}
							onChange={(e) => maj("title", e.target.value)}
							placeholder="Bienvenue chez toi"
						/>
					</FormRow>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<FormRow label="Sur-titre">
							<input
								className="aform-i"
								value={v.eyebrow}
								onChange={(e) => maj("eyebrow", e.target.value)}
								placeholder="Nouveauté · Mode"
							/>
						</FormRow>
						<FormRow label="Texte du bouton">
							<input
								className="aform-i"
								value={v.ctaLabel}
								onChange={(e) => maj("ctaLabel", e.target.value)}
							/>
						</FormRow>
					</div>

					<FormRow
						label="Où mène la bannière"
						helper="Adresse interne, ex. /bons-plans"
						required
					>
						<input
							className="aform-i"
							value={v.ctaHref}
							onChange={(e) => maj("ctaHref", e.target.value)}
							placeholder="/made-in-cameroon"
						/>
					</FormRow>

					{champs.product && (
						<FormRow label="Produit mis en avant" helper="Son identifiant d'URL">
							<input
								className="aform-i"
								value={v.productSlug}
								onChange={(e) => maj("productSlug", e.target.value)}
								placeholder="coffret-karite-prestige"
							/>
						</FormRow>
					)}

					{(champs.discount || champs.code) && (
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							{champs.discount && (
								<FormRow label="Réduction affichée">
									<input
										className="aform-i"
										value={v.discount}
										onChange={(e) => maj("discount", e.target.value)}
										placeholder="-40%"
									/>
								</FormRow>
							)}
							{champs.code && (
								<FormRow label="Code promo">
									<input
										className="aform-i"
										value={v.code}
										onChange={(e) => maj("code", e.target.value)}
										placeholder="MAISON40"
									/>
								</FormRow>
							)}
						</div>
					)}

					{champs.thumbs && (
						<FormRow
							label="Vignettes"
							helper="Trois couleurs ou adresses d'images, séparées par des virgules"
						>
							<input
								className="aform-i"
								value={v.thumbs}
								onChange={(e) => maj("thumbs", e.target.value)}
								placeholder="#D4A24C, #1E4D3B, #E8D9C1"
							/>
						</FormRow>
					)}

					{champs.categories && (
						<FormRow
							label="Rayons affichés"
							helper="Identifiants d'URL séparés par des virgules"
						>
							<input
								className="aform-i"
								value={v.categorySlugs}
								onChange={(e) => maj("categorySlugs", e.target.value)}
								placeholder="mode, beaute, maison, tech"
							/>
						</FormRow>
					)}

					<FormRow label="Couleur de fond">
						<div style={{ display: "flex", gap: 8 }}>
							{PALETTE.map((c) => (
								<button
									key={c}
									type="button"
									aria-label={`Couleur ${c}`}
									aria-pressed={v.color === c}
									onClick={() => maj("color", c)}
									style={{
										width: 30,
										height: 30,
										borderRadius: 8,
										background: c,
										cursor: "pointer",
										border:
											v.color === c
												? "2px solid var(--a-ink)"
												: "2px solid transparent",
									}}
								/>
							))}
						</div>
					</FormRow>

					<FormRow
						label="Bannière image (facultative)"
						helper="Remplace le texte composé — 1600 × 500"
					>
						<MediaUploader
							defaultRole="hero"
							label="Choisir un visuel"
							onChange={image}
						/>
					</FormRow>

					{v.imageUrl && (
						<FormRow
							label="Description de l'image"
							helper="Obligatoire pour publier"
							required
						>
							<input
								className="aform-i"
								value={v.imageAlt}
								onChange={(e) => maj("imageAlt", e.target.value)}
								placeholder="Femme portant une robe en pagne kente"
							/>
						</FormRow>
					)}

					<FormRow label="Emplacement">
						<select
							className="aform-i"
							value={v.placement}
							onChange={(e) => maj("placement", e.target.value as BannerPlacement)}
						>
							{Object.entries(PLACEMENT_LABELS).map(([k, l]) => (
								<option key={k} value={k}>
									{l}
								</option>
							))}
						</select>
					</FormRow>

					<div
						style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}
					>
						<FormRow label="Début">
							<input
								type="date"
								className="aform-i"
								value={v.startsAt}
								onChange={(e) => maj("startsAt", e.target.value)}
							/>
						</FormRow>
						<FormRow label="Fin">
							<input
								type="date"
								className="aform-i"
								value={v.endsAt}
								onChange={(e) => maj("endsAt", e.target.value)}
							/>
						</FormRow>
						<FormRow label="Durée d'affichage">
							<select
								className="aform-i"
								value={v.durationSec}
								onChange={(e) => maj("durationSec", Number(e.target.value))}
							>
								{[4, 5, 7, 10].map((s) => (
									<option key={s} value={s}>
										{s} secondes
									</option>
								))}
							</select>
						</FormRow>
					</div>
				</div>
			</div>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={v.title.trim().length < 2 || saving}
					onClick={valider}
				>
					<AdminIcon n="check" s={14} />
					{banner ? "Enregistrer" : "Créer la bannière"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
